import { forwardRef, useState, type ReactNode } from "react";
import { Bell, Building2, ChevronDown, CircleHelp, Globe, LogOut, Menu as MenuIcon, Monitor, Moon, Search, Settings, Sun } from "lucide-react";

import { Popover, Tooltip } from "@/components/ds";
import { cn } from "@/lib/cn";
import { useThemeMode, type ThemeMode } from "@/lib/theme";
import { Menu, MenuItem, MenuItems, MenuTrigger } from "./Menu";

// The app bar above the content column. From d2d-UEM ds-uem/. SIEM's equivalent is an
// app component (Topbar.tsx), not part of its ds/, so this is built for the
// cascade plan (task 2.9) from SIEM's rc-topbar styles, renamed rc-appheader.
//
// The props are @datasirpi/ui's AppHeader, name for name, so Phase 3 can swap
// the import without touching Layout.tsx. Visual choices follow SIEM's bar:
// quiet 4%-ink buttons, one 64px row sharing the rail's hairline, and the
// account menu holding theme, settings and sign-out, as SIEM's Topbar does.
// The bar carries no page title: each page states its own at the top of its
// content, so the bar is identical on every route. The help button and the
// account button carry the product tour's data-tour hooks.

export interface AppHeaderUser {
  name: string;
  /** Shown under the name in the account menu, as SIEM's role tag. */
  role?: string;
  /** Optional avatar image; falls back to the name's initials. */
  avatarUrl?: string;
}

/**
 * The tenant the console is acting in (design eval F17). DLP addition: the
 * shell is multi-tenant, and nothing used to say which tenant you were in.
 */
export interface AppHeaderTenant {
  /** The tenant's slug, as the sign-in form and the JWT carry it. */
  slug: string;
  /** Display name, when the caller knows it; the slug is shown otherwise. */
  name?: string;
  /** Platform scope: the user can act across tenants (platform staff). */
  platform?: boolean;
}

export interface AppHeaderProps {
  /** Title on the left (app or section name). */
  title?: ReactNode;
  /** Size of the title. Default "lg". */
  titleSize?: "sm" | "md" | "lg";
  /** Optional brand mark rendered before the title. */
  brand?: ReactNode;
  /** Show the search field. Default true. */
  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Centered (default) or pinned after the title. */
  searchAlign?: "center" | "start";
  onSearch?: (value: string) => void;
  /** Show the notifications button. Default true. */
  showNotifications?: boolean;
  /** Unread count, shown as a badge when above 0. */
  notificationCount?: number;
  onNotificationsClick?: () => void;
  /** DLP addition: content for a panel the bell opens (ds Popover). When
   *  set, the bell toggles the panel instead of calling onNotificationsClick. */
  notificationsPanel?: ReactNode;
  /** Controlled open state of the notifications panel. */
  notificationsOpen?: boolean;
  onNotificationsOpenChange?: (open: boolean) => void;
  /** Show the help button. Default true. */
  showHelp?: boolean;
  onHelpClick?: () => void;
  /** Show the light/dark switcher in the account menu. Default true. */
  showThemeSwitcher?: boolean;
  /** Current user: drives the avatar and the account menu. Omit to hide both. */
  user?: AppHeaderUser;
  onProfile?: () => void;
  onLogout?: () => void;
  /** Show the profile item in the account menu. Default true. */
  showProfile?: boolean;
  /** Show the sign-out item in the account menu. Default true. */
  showLogout?: boolean;
  searchLabel?: string;
  notificationsLabel?: string;
  helpLabel?: string;
  themeLabel?: string;
  profileLabel?: string;
  logoutLabel?: string;
  /** The current tenant, shown as a chip at the start of the bar on every
   *  route. Platform scope draws as its own chip (a globe and the word
   *  "Platform"), not as a recoloured tenant chip. DLP addition (F17). */
  tenant?: AppHeaderTenant;
  /** Renders the menu button (below lg) that opens the sidebar. */
  onToggleSidebar?: () => void;
  /**
   * Extra content at the start of the right-hand group, before the
   * notifications and help buttons: a clock, a status chip. DLP addition
   * (cascade plan 3.2).
   */
  actions?: ReactNode;
  "aria-label"?: string;
  className?: string;
}

const THEME_OPTIONS = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
] as const satisfies ReadonlyArray<{ value: ThemeMode; label: string; icon: unknown }>;

interface AccountMenuProps {
  user: AppHeaderUser;
  showThemeSwitcher: boolean;
  themeLabel: string;
  onProfile?: () => void;
  profileLabel: string;
  onLogout?: () => void;
  logoutLabel: string;
}

// The theme modes as menuitemradio items. Its own component so useThemeMode
// is only read while the menu is open, as ds/ThemeSwitcher was.
function ThemeItems({ label }: { label: string }) {
  const { mode, resolvedMode, setMode } = useThemeMode();
  return (
    <div role="group" aria-label={label} className="rc-appheader__menu-row rc-appheader__menu-row--static">
      <span aria-hidden="true">Theme</span>
      <span className="rc-theme-switcher rc-appheader__theme">
        {THEME_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const name = opt.value === "system" ? `${opt.label} (currently ${resolvedMode})` : opt.label;
          return (
            <Tooltip key={opt.value} content={opt.value === "system" ? `${opt.label} — currently ${resolvedMode}` : opt.label}>
              <button
                type="button"
                role="menuitemradio"
                tabIndex={-1}
                aria-checked={mode === opt.value}
                aria-label={name}
                className="rc-theme-switcher__option"
                onClick={(e) => {
                  // Same wipe origin ds/ThemeSwitcher sets, so the
                  // transition starts from the segment pressed.
                  const r = e.currentTarget.getBoundingClientRect();
                  const root = document.documentElement;
                  root.style.setProperty("--theme-wipe-x", `${r.left + r.width / 2}px`);
                  root.style.setProperty("--theme-wipe-y", `${r.top + r.height / 2}px`);
                  setMode(opt.value);
                }}
              >
                <Icon aria-hidden="true" />
              </button>
            </Tooltip>
          );
        })}
      </span>
    </div>
  );
}

// Eval F29: the account menu is the ds-dlp Menu (one Tab stop, arrow keys,
// Escape back to the button) rather than a hand-built popover of buttons.
// A role="menu" may only hold items, groups and separators, so:
// - the name and role tag are drawn as before but hidden from the a11y tree;
//   the menu's own name carries them ("Account, Jane Doe (Admin)").
// - ds/ThemeSwitcher is a toolbar, which cannot sit inside a menu (its arrow
//   keys would fight the menu's). The three modes become menuitemradio items
//   in a "Theme" group, drawn with the switcher's own segmented styles. Arrow
//   Up/Down walk through them like any item; choosing one keeps the menu open
//   so the change can be seen and undone.
function AccountMenu({ user, showThemeSwitcher, themeLabel, onProfile, profileLabel, onLogout, logoutLabel }: AccountMenuProps) {
  return (
    <Menu align="end">
      <MenuTrigger>
        <button type="button" className="rc-appheader__button rc-appheader__account" aria-label={`Account menu, ${user.name}`} data-tour="theme">
          <span className="rc-appheader__avatar" aria-hidden="true">
            {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(user.name)}
          </span>
          <span className="rc-appheader__user-name">{user.name}</span>
          <ChevronDown className="rc-appheader__chevron" aria-hidden="true" />
        </button>
      </MenuTrigger>
      <MenuItems className="rc-appheader__menu" aria-label={`Account, ${user.name}${user.role ? ` (${user.role})` : ""}`}>
        <div className="rc-appheader__menu-header" aria-hidden="true">
          <span className="rc-appheader__menu-name">{user.name}</span>
          {user.role ? <span className="rc-appheader__menu-role">{user.role}</span> : null}
        </div>
        <div className="rc-appheader__menu-divider" role="separator" />
        {showThemeSwitcher ? <ThemeItems label={themeLabel} /> : null}
        {onProfile ? (
          <MenuItem onSelect={onProfile} shortcut={<Settings aria-hidden="true" />}>
            {profileLabel}
          </MenuItem>
        ) : null}
        {onLogout ? (
          <>
            <div className="rc-appheader__menu-divider" role="separator" />
            <MenuItem onSelect={onLogout} shortcut={<LogOut aria-hidden="true" />}>
              {logoutLabel}
            </MenuItem>
          </>
        ) : null}
      </MenuItems>
    </Menu>
  );
}

// Eval F17: which tenant the console is acting in, on every route. A customer
// tenant is a hairline pill with a building and the tenant's name. Platform
// scope (a user who can act across tenants) is a filled pill with a globe and
// the word "Platform" before the tenant, so the two differ in shape, glyph
// and words, not only in colour. It is information, not a control: no role,
// but focusable so its tooltip (what the scope means) is reachable by
// keyboard, as the agent status labels are (F28). The words a screen reader
// hears are in the text itself ("Tenant: acme", "Platform scope, tenant acme").
function TenantChip({ tenant }: { tenant: AppHeaderTenant }) {
  const label = tenant.name?.trim() || tenant.slug;
  const named = label !== tenant.slug;
  const platform = !!tenant.platform;
  const Icon = platform ? Globe : Building2;
  const tip = platform
    ? `Platform scope: you can manage every tenant. Tenant data here is from ${named ? `${label} (${tenant.slug})` : label}.`
    : `Signed in to tenant ${named ? `${label} (${tenant.slug})` : label}.`;
  return (
    <Tooltip content={tip} side="bottom">
      <span tabIndex={0} className="rc-appheader__tenant" data-scope={platform ? "platform" : "tenant"} data-testid="tenant-chip">
        <Icon aria-hidden="true" />
        {platform ? (
          <>
            <span className="rc-appheader__tenant-scope">
              Platform<span className="sr-only"> scope, </span>
            </span>
            <span className="rc-appheader__tenant-sep" aria-hidden="true" />
            <span className="rc-appheader__tenant-name">
              <span className="sr-only">tenant </span>
              {label}
            </span>
          </>
        ) : (
          <span className="rc-appheader__tenant-name">
            <span className="sr-only">Tenant: </span>
            {label}
          </span>
        )}
      </span>
    </Tooltip>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const AppHeader = forwardRef<HTMLElement, AppHeaderProps>(function AppHeader(
  {
    title,
    titleSize = "lg",
    brand,
    showSearch = true,
    searchPlaceholder = "Search",
    searchAlign = "center",
    onSearch,
    showNotifications = true,
    notificationCount = 0,
    onNotificationsClick,
    notificationsPanel,
    notificationsOpen,
    onNotificationsOpenChange,
    showHelp = true,
    onHelpClick,
    showThemeSwitcher = true,
    user,
    onProfile,
    onLogout,
    showProfile = true,
    showLogout = true,
    searchLabel,
    notificationsLabel = "Notifications",
    helpLabel = "Help",
    themeLabel = "Theme",
    profileLabel = "Profile",
    logoutLabel = "Sign out",
    onToggleSidebar,
    tenant,
    actions,
    "aria-label": ariaLabel = "Application header",
    className,
  },
  ref
) {
  const [query, setQuery] = useState("");
  const unread = notificationCount > 0;
  const hasMenu = user && (showThemeSwitcher || (showProfile && onProfile) || (showLogout && onLogout));

  const search = showSearch ? (
    <label className={cn("rc-appheader__search", searchAlign === "center" && "rc-appheader__search--center")}>
      <Search aria-hidden="true" />
      <input
        type="search"
        value={query}
        placeholder={searchPlaceholder}
        aria-label={searchLabel ?? searchPlaceholder}
        onChange={(e) => {
          setQuery(e.target.value);
          onSearch?.(e.target.value);
        }}
      />
    </label>
  ) : null;

  return (
    <header ref={ref} className={cn("rc-appheader", className)} aria-label={ariaLabel}>
      <div className="rc-appheader__inner">
        <div className="rc-appheader__left">
          {onToggleSidebar ? (
            <button
              type="button"
              className="rc-appheader__button rc-appheader__button--icon rc-appheader__menu-toggle"
              aria-label="Open navigation"
              onClick={onToggleSidebar}
            >
              <MenuIcon aria-hidden="true" />
            </button>
          ) : null}
          {brand ? <span className="rc-appheader__brand">{brand}</span> : null}
          {title ? (
            <h1 className="rc-appheader__title" data-size={titleSize}>
              {title}
            </h1>
          ) : null}
          {tenant ? <TenantChip tenant={tenant} /> : null}
          {searchAlign === "start" ? search : null}
        </div>

        {searchAlign === "center" ? search : null}

        <div className="rc-appheader__right">
          {actions}
          {showNotifications ? (() => {
            const bell = (
              <button
                type="button"
                className="rc-appheader__button rc-appheader__button--icon"
                aria-label={unread ? `${notificationsLabel}, ${notificationCount} unread` : notificationsLabel}
                onClick={notificationsPanel ? undefined : onNotificationsClick}
              >
                <Bell aria-hidden="true" />
                {unread ? (
                  <span className="rc-appheader__badge" aria-hidden="true">
                    {notificationCount > 99 ? "99+" : notificationCount}
                  </span>
                ) : null}
              </button>
            );
            return notificationsPanel ? (
              <Popover
                side="bottom"
                align="end"
                open={notificationsOpen}
                onOpenChange={onNotificationsOpenChange}
                aria-label={notificationsLabel}
                content={notificationsPanel}
              >
                {bell}
              </Popover>
            ) : bell;
          })() : null}

          {showHelp ? (
            <button
              type="button"
              className="rc-appheader__button rc-appheader__button--icon"
              aria-label={helpLabel}
              data-tour="help"
              onClick={onHelpClick}
            >
              <CircleHelp aria-hidden="true" />
            </button>
          ) : null}

          {user ? <span className="rc-appheader__divider" aria-hidden="true" /> : null}
          {user && hasMenu ? (
            <AccountMenu
              user={user}
              showThemeSwitcher={showThemeSwitcher}
              themeLabel={themeLabel}
              onProfile={showProfile ? onProfile : undefined}
              profileLabel={profileLabel}
              onLogout={showLogout ? onLogout : undefined}
              logoutLabel={logoutLabel}
            />
          ) : user ? (
            <span className="rc-appheader__button rc-appheader__account" aria-label={user.name}>
              <span className="rc-appheader__avatar" aria-hidden="true">
                {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(user.name)}
              </span>
              <span className="rc-appheader__user-name">{user.name}</span>
            </span>
          ) : null}
        </div>
      </div>
    </header>
  );
});
